import { useCallback, useEffect, useState, type ReactNode } from "react";

import { ApiError } from "../lib/api";
import { useAbortableConfirm } from "../lib/useAbortableConfirm";
import { formatDate } from "../lib/dateFormat";
import { formatMoney } from "../lib/money";
import { t123MembershipApi, type T123PlayerMembership } from "../lib/t123MembershipApi";

type Props = {
  token: string;
  clubId: number;
  playerProfileId?: number | null;
  /** Para pruebas y vista previa: datos ya cargados. */
  initialData?: T123PlayerMembership | null;
  /** Lo que se ve mientras el cobro está apagado. */
  fallback?: ReactNode;
  /** Avisa si el cobro está encendido (para no repetir «Al corriente» arriba). */
  onBillingActive?: (active: boolean) => void;
  /** Estado de la membresía (null si el cobro está apagado), para el encabezado de «Mi club». */
  onStanding?: (standing: T123PlayerMembership["standing"] | null) => void;
};

function tone(code: string): "ok" | "warn" | "bad" | "neutral" {
  if (code === "active" || code === "cancelled_active") return "ok";
  if (code === "grace" || code === "renewal_pending") return "warn";
  if (code === "exempt" || code === "club_pays") return "neutral";
  return "bad";
}

/** Tarjeta «Membresía Torneos123» dentro de cada club del jugador. */
export default function T123MembershipCard({ token, clubId, playerProfileId, initialData = null, fallback = null, onBillingActive, onStanding }: Props) {
  const { askConfirm } = useAbortableConfirm([clubId, playerProfileId]);
  const [data, setData] = useState<T123PlayerMembership | null>(initialData);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const resp = await t123MembershipApi.player(token, clubId, playerProfileId);
      setData(resp.data);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "No pudimos cargar tu membresía.");
    }
  }, [token, clubId, playerProfileId]);

  useEffect(() => {
    if (!initialData) void load();
  }, [initialData, load]);

  const billingActive = Boolean(data?.billing_enabled);
  useEffect(() => {
    onBillingActive?.(billingActive);
  }, [billingActive, onBillingActive]);
  const standingForParent = billingActive ? (data?.standing ?? null) : null;
  useEffect(() => {
    onStanding?.(standingForParent);
  }, [standingForParent, onStanding]);

  if (!data || !data.billing_enabled) return <>{fallback}</>;
  const { standing, membership } = data;
  const code = standing.code;
  const price = formatMoney(data.price, data.currency);

  async function goToMercadoPago(request: () => Promise<{ data: { init_point: string } }>) {
    setBusy(true);
    setError(null);
    try {
      const resp = await request();
      window.location.assign(resp.data.init_point);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "No se pudo abrir Mercado Pago.");
      setBusy(false);
    }
  }

  async function handleCancel() {
    if (!membership) return;
    const until = formatDate(membership.paid_until, "");
    const ok = await askConfirm(
      until
        ? `¿Cancelar tu membresía? Podrás seguir reservando hasta el ${until}; después ya no se cobra.`
        : "¿Cancelar tu membresía? Ya no se cobrará.",
      { acceptLabel: "Sí, cancelar" },
    );
    if (!ok) return;
    setBusy(true);
    setError(null);
    try {
      const resp = await t123MembershipApi.cancel(token, membership.id);
      setData(resp.data);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "No se pudo cancelar.");
    } finally {
      setBusy(false);
    }
  }

  const subscribe = () => goToMercadoPago(() => t123MembershipApi.subscribe(token, clubId, playerProfileId));
  const changeCard = () => membership && goToMercadoPago(() => t123MembershipApi.changeCard(token, membership.id));

  let detail: string;
  if (code === "exempt") detail = "Este club no cobra membresía.";
  else if (code === "club_pays") detail = "La membresía la paga el club.";
  else if (code === "active")
    detail = `Próximo cobro: ${formatDate(membership?.next_payment_date ?? membership?.paid_until, "por confirmar")}`;
  else if (code === "cancelled_active") detail = `Cancelada. Puedes reservar hasta el ${formatDate(membership?.paid_until)}.`;
  else if (code === "grace")
    detail = `No pudimos cobrar tu mes. Puedes reservar hasta el ${formatDate(standing.grace_until)}; revisa tu tarjeta.`;
  else if (code === "renewal_pending")
    detail = "Tu mes venció; Mercado Pago está por cobrar la renovación. Puedes seguir reservando.";
  else if (code === "suspended") detail = "Suspendido por falta de pago. Actualiza tu tarjeta para volver a reservar.";
  else if (code === "no_booking") detail = "Falta tu pago. No puedes reservar hasta que se cobre.";
  else if (membership?.status === "pending") detail = "Falta completar el pago en Mercado Pago.";
  else detail = `Actívala para reservar canchas: ${price} al mes, se cobra solo cada mes.`;

  const hasLiveSubscription = membership && membership.status !== "cancelled" && membership.status !== "pending";

  return (
    <section className={`t123-membership-card t123-membership-card--${tone(code)}`} aria-label="Membresía Torneos123">
      <div className="t123-membership-card__head">
        <span className="t123-membership-card__title">Membresía Torneos123</span>
        <span className="t123-membership-card__badge">{standing.label}</span>
      </div>
      <p className="t123-membership-card__detail">{detail}</p>
      {hasLiveSubscription && membership?.card_last4 ? (
        <p className="t123-membership-card__meta">
          Tarjeta terminación {membership.card_last4} · {price} al mes
        </p>
      ) : null}
      {membership?.card_change_pending ? (
        <p className="t123-membership-card__meta">Cambio de tarjeta en proceso en Mercado Pago.</p>
      ) : null}
      {error ? (
        <p className="form-error" role="alert">
          {error}
        </p>
      ) : null}
      <div className="t123-membership-card__actions">
        {(code === "unpaid" || code === "cancelled" || code === "cancelled_active") && (
          <button type="button" className="btn btn--compact" disabled={busy} onClick={() => void subscribe()}>
            {membership?.status === "pending" && membership.init_point
              ? "Continuar pago"
              : code === "unpaid"
                ? `Activar (${price}/mes)`
                : "Volver a activar"}
          </button>
        )}
        {hasLiveSubscription && (
          <button type="button" className="btn btn--compact btn--ghost" disabled={busy} onClick={() => void changeCard()}>
            Cambiar tarjeta
          </button>
        )}
        {hasLiveSubscription && (
          <button type="button" className="btn btn--compact btn--ghost" disabled={busy} onClick={() => void handleCancel()}>
            Cancelar membresía
          </button>
        )}
      </div>
    </section>
  );
}
